import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useParams } from 'react-router-dom';
import tokens from '../../styles/tokens';
import SeverityBadge from './SeverityBadge';
import { useNotifications } from '../../hooks/useNotifications';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [clusterSearch, setClusterSearch] = useState('');
  const { notifications, markAsRead, markAllRead, summaryByCluster, refreshNotifications } = useNotifications();
  const { clusterName } = useParams();
  const hasFetchedRef = useRef(false);
  const normalizedClusterName = clusterName ? decodeURIComponent(clusterName) : null;
  const scopedNotifications = useMemo(() => {
    if (!normalizedClusterName) {
      return notifications;
    }
    const target = normalizedClusterName.toLowerCase();
    return notifications.filter((item) => item.cluster?.toLowerCase() === target);
  }, [normalizedClusterName, notifications]);
  const unreadCount = useMemo(() => {
    if (normalizedClusterName && summaryByCluster[normalizedClusterName]) {
      return summaryByCluster[normalizedClusterName].unread_count || 0;
    }
    const summaries = Object.values(summaryByCluster || {});
    if (summaries.length) {
      return summaries.reduce((total, summary) => total + (summary.unread_count || 0), 0);
    }
    return scopedNotifications.filter((item) => !item.isRead).length;
  }, [normalizedClusterName, scopedNotifications, summaryByCluster]);

  const previewItems = useMemo(() => {
    const term = clusterSearch.trim().toLowerCase();
    return scopedNotifications
      .filter((item) => (
        !term || item.cluster?.toLowerCase().includes(term)
      ))
      .sort((a, b) => dayjs(b.dataTimestamp || b.time || b.createdAt).valueOf() - dayjs(a.dataTimestamp || a.time || a.createdAt).valueOf());
  }, [clusterSearch, scopedNotifications]);
  const severityTone = (severity) => ({
    healthy: '#2e7d32',
    warning: '#f9a825',
    degraded: '#fb8c00',
    critical: '#d81b60',
    severe: '#c62828'
  }[severity] || '#5e35b1');
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Fetch notifications on initial mount only (not on every navigation)
  useEffect(() => {
    // Only fetch once globally, not on every cluster change or navigation
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    // Initial fetch - refreshNotifications will handle checking if data exists
    if (normalizedClusterName) {
      refreshNotifications(normalizedClusterName);
    } else {
      refreshNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    // Only fetch if we don't have notifications for this cluster yet
    if (normalizedClusterName) {
      const hasClusterNotifications = notifications.some((item) => item.cluster === normalizedClusterName);
      if (!hasClusterNotifications) {
        refreshNotifications(normalizedClusterName);
      }
      return;
    }
    if (notifications.length === 0) {
      refreshNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedClusterName, open]);

  // Open notification in new tab
  const openInvestigatePage = (item) => {
    markAsRead(item.id);
    handleClose();
    const url = `/investigate/${encodeURIComponent(item.cluster)}/${item.id}`;
    window.open(url, '_blank');
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label="notifications"
        onClick={handleOpen}
        sx={{
          color: '#64748B',
          borderRadius: 1.5,
          width: 36,
          height: 36,
          '&:hover': { backgroundColor: '#F1F5F9' },
        }}
      >
        <Badge color="error" badgeContent={unreadCount > 99 ? '99+' : unreadCount}>
          <NotificationsNoneIcon sx={{ fontSize: 20 }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, boxShadow: tokens.colors.shadow } }}
      >
        <Box sx={{ padding: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Notifications
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => markAllRead(normalizedClusterName)}
                disabled={unreadCount === 0}
              >
                Mark all read
              </Button>
              <Button
                size="small"
                onClick={() => {
                  handleClose();
                  if (normalizedClusterName) {
                    navigate(`/notifications?cluster=${encodeURIComponent(normalizedClusterName)}`);
                  } else {
                    navigate('/notifications');
                  }
                }}
              >
                View All
              </Button>
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ paddingX: 2, paddingBottom: 1 }}>
          <Typography variant="caption" sx={{ color: tokens.colors.textMuted }}>
            Cluster name
          </Typography>
          <Box
            component="input"
            value={clusterSearch}
            onChange={(event) => setClusterSearch(event.target.value)}
            placeholder="e.g. TriCountiesBank"
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 8,
              border: `1px solid ${tokens.colors.border}`,
              outline: 'none',
              fontSize: '0.85rem'
            }}
          />
        </Box>
        <Divider />
        <List sx={{ padding: 0, maxHeight: 360, overflowY: 'auto' }}>
          {previewItems.length === 0 ? (
            <Box sx={{ padding: 2 }}>
              <Typography variant="body2" sx={{ color: tokens.colors.textMuted }}>
                No notifications yet.
              </Typography>
            </Box>
          ) : (
            previewItems.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => openInvestigatePage(item)}
                sx={{
                  paddingY: 1.5,
                  paddingX: 2,
                  borderLeft: `4px solid ${severityTone(item.severity)}`
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: item.isRead ? 'transparent' : tokens.colors.primary
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {item.cluster}
                        </Typography>
                        <SeverityBadge severity={item.severity} />
                        <Box
                          sx={{
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.75,
                            backgroundColor: item.type === 'Rescan' ? '#FEF3C7' : '#EEF2FF',
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            color: item.type === 'Rescan' ? '#92400E' : '#4F46E5',
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em'
                          }}
                        >
                          {item.type || 'Scan'}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a73e8' }}>
                          {item.performanceScore ?? '--'}/100
                        </Typography>
                        <OpenInNewIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ color: tokens.colors.textMuted, pl: '20px' }}>
                      {dayjs(item.dataTimestamp || item.time || item.createdAt).format('MMM D, YYYY h:mm A')}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))
          )}
        </List>
      </Popover>
    </>
  );
};

export default NotificationBell;
